import { useCallback } from 'react';
import { toast } from 'sonner';

import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useJobProgress } from '@/hooks/useJobProgress';
import { useUpdateSeed } from '@/hooks/useSeeds';
import { ResourceType, Seed, SeedStatus } from '@/types';
import { pluralize } from '@/utils/pluralize.util';

/**
 * Return type for useSeedStatusHandlers hook
 */
export interface SeedStatusHandlers {
  /** Update status for multiple seeds with confirmation */
  handleStatusUpdate: (
    seeds: Seed[],
    status: SeedStatus,
    seedType: 'domain' | 'ip' | 'webapplication'
  ) => Promise<void>;
  /** Approve seeds (Accept status) */
  handleApprove: (
    seeds: Seed[],
    seedType: 'domain' | 'ip' | 'webapplication'
  ) => Promise<void>;
  /** Reject seeds */
  handleReject: (
    seeds: Seed[],
    seedType: 'domain' | 'ip' | 'webapplication'
  ) => Promise<void>;
  /** Freeze seeds */
  handleFreeze: (
    seeds: Seed[],
    seedType: 'domain' | 'ip' | 'webapplication'
  ) => Promise<void>;
  /** Delete seeds */
  handleDelete: (
    seeds: Seed[],
    seedType: 'domain' | 'ip' | 'webapplication'
  ) => Promise<void>;
}

/**
 * Hook for managing seed status updates
 *
 * Provides handlers for all seed status operations with proper error handling,
 * progress tracking, and search invalidation.
 *
 * Features:
 * - Promise.allSettled for partial failure handling
 * - Progress tracking for long operations
 * - Generic search invalidation
 * - Success/failure toast notifications
 * - Type-aware seed updates (asset vs webapplication)
 *
 * @example
 * ```tsx
 * const { handleApprove, handleReject, handleStatusUpdate } = useSeedStatusHandlers();
 *
 * // Approve selected seeds
 * await handleApprove(selectedSeeds, 'domain');
 *
 * // Update status with custom value
 * await handleStatusUpdate(seeds, SeedStatus.Freeze, 'webapplication');
 * ```
 */
export const useSeedStatusHandlers = ({
  selectionState,
  onBulkRequested,
}: {
  selectionState?: BulkSelectionState;
  onBulkRequested?: (status: SeedStatus) => void;
} = {}): SeedStatusHandlers => {
  const { mutateAsync: updateSeed } = useUpdateSeed({ invalidateSeed: true });
  const { invalidate: invalidateGenericSearch } = useGenericSearch(
    {
      query: '',
      label: ResourceType.Seed,
    },
    { enabled: false }
  );
  const { startProgress } = useJobProgress();

  /**
   * Update status for multiple seeds
   */
  const handleStatusUpdate = useCallback(
    async (
      seeds: Seed[],
      status: SeedStatus,
      _seedType: 'domain' | 'ip' | 'webapplication'
    ) => {
      if (selectionState && shouldUseBulk(selectionState) && onBulkRequested) {
        onBulkRequested(status);
        return;
      }

      if (seeds.length === 0) return;

      startProgress();

      const promises = seeds.map(({ key, identifier, class: seedClass }) =>
        updateSeed({
          key,
          identifier,
          status,
          type: seedClass === 'webapplication' ? 'webapplication' : 'asset',
        })
      );

      const results = await Promise.allSettled(promises);

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (succeeded > 0) {
        toast.success(
          `Status updated for ${succeeded} ${pluralize(succeeded, 'seed')}`
        );
      }

      if (failed > 0) {
        toast.error(`Failed to update ${failed} ${pluralize(failed, 'seed')}`);
      }

      // Invalidate search cache
      invalidateGenericSearch();
    },
    [
      updateSeed,
      invalidateGenericSearch,
      startProgress,
      selectionState,
      onBulkRequested,
    ]
  );

  /**
   * Approve seeds (Accept status)
   */
  const handleApprove = useCallback(
    async (seeds: Seed[], seedType: 'domain' | 'ip' | 'webapplication') => {
      await handleStatusUpdate(seeds, SeedStatus.Accept, seedType);
    },
    [handleStatusUpdate]
  );

  /**
   * Reject seeds
   */
  const handleReject = useCallback(
    async (seeds: Seed[], seedType: 'domain' | 'ip' | 'webapplication') => {
      await handleStatusUpdate(seeds, SeedStatus.Reject, seedType);
    },
    [handleStatusUpdate]
  );

  /**
   * Freeze seeds
   */
  const handleFreeze = useCallback(
    async (seeds: Seed[], seedType: 'domain' | 'ip' | 'webapplication') => {
      await handleStatusUpdate(seeds, SeedStatus.Freeze, seedType);
    },
    [handleStatusUpdate]
  );

  /**
   * Delete seeds
   */
  const handleDelete = useCallback(
    async (seeds: Seed[], seedType: 'domain' | 'ip' | 'webapplication') => {
      await handleStatusUpdate(seeds, SeedStatus.Delete, seedType);
    },
    [handleStatusUpdate]
  );

  return {
    handleStatusUpdate,
    handleApprove,
    handleReject,
    handleFreeze,
    handleDelete,
  };
};
